/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Button, Hidden, SelectField, SelectOption, Tab, TabContext, Tabs, useTheme } from '@coursera/cds-core';
import { ChevronNextIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const useStyles = () => {
  const { spacing, palette } = useTheme();
  return {
    verticalTabs: css`
      display: flex;
      flex-direction: column;
    `,
    tabButton: css`
      justify-content: start;
      color: ${palette.black[500]};
    `,
    selectedTabButton: css`
      color: ${palette.blue[600]};

      /* we need to override the value of the background to make it look closer to a tab */
      background: ${palette.gray[100]} !important;
    `,
    buttonArrow: css`
      margin-left: ${spacing(12)};
    `,
  };
};

type Props<T extends string> = {
  tabs: {
    [key in T]: string;
  };
  onTabSelected: (id: T) => void;
  selectedTab: T;
};

const ResponsiveTab = <T extends string>({ tabs, onTabSelected, selectedTab }: Props<T>) => {
  const styles = useStyles();

  const ids = Object.keys(tabs) as T[];

  return (
    <div role="tablist">
      <Hidden mdDown>
        <div css={styles.verticalTabs}>
          {ids.map((id) => (
            <Button
              role="tab"
              css={[styles.tabButton, selectedTab === id ? styles.selectedTabButton : null]}
              variant="ghost"
              key={id}
              onClick={() => onTabSelected(id)}
              aria-selected={selectedTab === id}
            >
              {tabs[id]}
              {selectedTab === id && <ChevronNextIcon size="small" css={styles.buttonArrow} />}
            </Button>
          ))}
        </div>
      </Hidden>
      <Hidden xsDown lgUp>
        <TabContext value={selectedTab}>
          <Tabs aria-label={_t('Tabs')} onChange={(value) => onTabSelected(value as T)}>
            {ids.map((id) => (
              <Tab key={id} label={tabs[id]} value={id} />
            ))}
          </Tabs>
        </TabContext>
      </Hidden>
      <Hidden smUp>
        <SelectField
          fullWidth
          value={selectedTab}
          aria-label={_t('Select tab')}
          onChange={(e) => onTabSelected(e.target.value as T)}
        >
          {ids.map((id) => (
            <SelectOption key={id} value={id}>
              {tabs[id]}
            </SelectOption>
          ))}
        </SelectField>
      </Hidden>
    </div>
  );
};

export default ResponsiveTab;
